import React, { useState, useEffect, useCallback, useRef } from 'react';
import { withTranslation } from '../ChildrenContext.js';
import PropTypes from 'prop-types';
import Multiselect from 'react-widgets/Multiselect';
import typeOf from '../../helpers/typeOf';
import MultiValueView from './MultiValueView';

// numericOnly: allow only digits; "1, 2 3" typed or pasted is split into separate values
const Custom = ({ segment, values = [], onAction, numericOnly = false, t }) => {
    const parseData = useCallback((valuesArray) => {
        if (!valuesArray || !Array.isArray(valuesArray)) {
            return [];
        }

        if (valuesArray.length && !typeOf.isObject(valuesArray[0])) {
            valuesArray = valuesArray.map((item) => {return {value: item, title: item}});
        } else if (valuesArray.length && typeOf.isObject(valuesArray[0]) && !valuesArray[0].title) {
            valuesArray = valuesArray.map((item) => {return {value: item.value, title: item.value}});
        }

        return valuesArray;
    }, []);

    const searchTermRef = useRef('');
    const onActionRef = useRef(onAction);

    const [state, setState] = useState(() => ({
        segment: segment,
        values: parseData(values),
        searchTerm: ''
    }));

    useEffect(() => {
        onActionRef.current = onAction;
    }, [onAction]);

    const valuesRef = useRef(state.values);
    useEffect(() => {
        valuesRef.current = state.values;
    }, [state.values]);

    // Values are computed outside the state updaters and passed to the parent's onAction after setState:
    // calling onAction inside an updater makes React warn about updating another component while rendering.
    const applyValues = useCallback((newValues) => {
        valuesRef.current = newValues;
        setState(prevState => ({
            ...prevState,
            values: newValues,
            searchTerm: ''
        }));
        onActionRef.current(newValues);
    }, []);

    const onChange = useCallback((value) => {
        // the pending text is either added here or dropped; it must not come back on blur
        const term = (searchTermRef.current || '').trim();
        searchTermRef.current = '';
        let finalValues = value;

        if (term && !(numericOnly && !/^\d+$/.test(term))) {
            const exists = value.some(item => item && item.value === term);
            if (!exists) {
                finalValues = [...value, { value: term, title: term }];
            }
        }

        applyValues(finalValues);
    }, [applyValues, numericOnly]);

    const onCreate = useCallback((value) => {
        if (numericOnly && !/^\d+$/.test(String(value).trim())) {
            return;
        }
        searchTermRef.current = '';
        applyValues([...valuesRef.current, { value, title: value }]);
    }, [numericOnly, applyValues]);

    const onSearch = useCallback((rawSearchTerm) => {
        let searchTerm = rawSearchTerm;

        if (numericOnly) {
            const parts = String(rawSearchTerm || '').split(/[\s,;]+/);
            // everything before the last separator is complete values
            const complete = parts.slice(0, -1).map(part => part.replace(/\D/g, '')).filter(Boolean);
            searchTerm = (parts[parts.length - 1] || '').replace(/\D/g, '');

            if (complete.length) {
                // computed outside the state updater: calling the parent's onAction from inside an updater
                // makes React warn about updating another component while rendering
                const currentValues = valuesRef.current;
                const existing = new Set(currentValues.map(item => item && item.value));
                const added = complete
                    .filter(value => !existing.has(value))
                    .filter((value, i, arr) => arr.indexOf(value) === i)
                    .map(value => ({ value, title: value }));
                if (added.length) {
                    const newValues = [...currentValues, ...added];
                    valuesRef.current = newValues;
                    setState(prevState => ({ ...prevState, values: newValues }));
                    onActionRef.current(newValues);
                }
            }
        }

        searchTermRef.current = searchTerm;
        setState(prevState => ({
            ...prevState,
            searchTerm
        }));
    }, [numericOnly]);

    const commitSearchTerm = useCallback(() => {
        const term = (searchTermRef.current || '').trim();
        // reset right away: otherwise the already committed text was added again on the next blur,
        // e.g. after the value had been removed with its "x"
        searchTermRef.current = '';
        if (term) {
            const currentValues = valuesRef.current;
            const exists = currentValues.some(item => item && item.value === term);
            applyValues(exists ? currentValues : [...currentValues, { value: term, title: term }]);
        }
    }, [applyValues]);

    const onBlur = useCallback(() => {
        commitSearchTerm();
    }, [commitSearchTerm]);

    // Jira-like editing: collapsed one-line view <-> editor with apply/cancel.
    // Values are passed to onAction while editing (so the filter's Apply always sees them);
    // cancel restores the values the editor was opened with.
    const wrapperRef = useRef(null);
    const snapshotRef = useRef(null);
    const [editing, setEditing] = useState(() => !parseData(values).length);
    const [focusOnEdit, setFocusOnEdit] = useState(false);
    // apply/cancel buttons appear only after the user starts working with the field
    // (an empty field is shown as the editor right away, e.g. a just added filter - no buttons yet)
    const [active, setActive] = useState(false);
    const activate = useCallback(() => setActive(true), []);

    const startEdit = useCallback(() => {
        snapshotRef.current = state.values;
        setFocusOnEdit(true);
        setActive(true);
        setEditing(true);
    }, [state.values]);

    const finishEdit = useCallback(() => {
        commitSearchTerm();
        snapshotRef.current = null;
        setActive(false);
        setEditing(false);
    }, [commitSearchTerm]);

    const cancelEdit = useCallback(() => {
        const snapshot = snapshotRef.current || [];
        searchTermRef.current = '';
        snapshotRef.current = null;
        applyValues(snapshot);
        setActive(false);
        setEditing(false);
    }, [applyValues]);

    const finishEditRef = useRef(finishEdit);
    useEffect(() => {
        finishEditRef.current = finishEdit;
    }, [finishEdit]);

    // a click / tap outside the editor closes it and keeps the values
    useEffect(() => {
        if (!editing) {
            return undefined;
        }
        const onPointerDown = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                finishEditRef.current();
            }
        };
        document.addEventListener('mousedown', onPointerDown, true);
        document.addEventListener('touchstart', onPointerDown, true);
        return () => {
            document.removeEventListener('mousedown', onPointerDown, true);
            document.removeEventListener('touchstart', onPointerDown, true);
        };
    }, [editing]);

    useEffect(() => {
        if (editing && focusOnEdit && wrapperRef.current) {
            const input = wrapperRef.current.querySelector('input');
            input && input.focus();
            setFocusOnEdit(false);
        }
    }, [editing, focusOnEdit]);

    const onKeyDown = useCallback((event) => {
        if (event.key === 'Enter' || event.keyCode === 13) {
            const term = (searchTermRef.current || '').trim();
            event.preventDefault();
            event.stopPropagation();
            if (term) {
                commitSearchTerm();
            } else {
                // Enter on an empty input = apply
                finishEdit();
            }
        } else if (event.key === 'Escape' || event.keyCode === 27) {
            event.preventDefault();
            event.stopPropagation();
            cancelEdit();
        }
    }, [commitSearchTerm, finishEdit, cancelEdit]);

    const messageCreateOption = useCallback((inst) => {
        const messages = (t && t.multiselect) || {};

        return [
            messages.createOption,
            inst.searchTerm && ' ',
            <strong key={'create'}>"{inst.searchTerm}"</strong>
        ];
    }, [t]);

    useEffect(() => {
        if (values !== state.values || segment !== state.segment) {
            setState(prevState => ({
                ...prevState,
                segment: segment,
                values: parseData(values)
            }));
        }
    }, [values, segment, parseData, state.values, state.segment]);

    useEffect(() => {
        const styleId = 'custom-multiselect-hide-popup';
        let styleElement = document.getElementById(styleId);

        if (state.searchTerm) {
            if (!styleElement) {
                styleElement = document.createElement('style');
                styleElement.id = styleId;
                document.head.appendChild(styleElement);
            }
            styleElement.textContent = '.rw-popup-container { display: none !important; }';
        } else {
            if (styleElement) {
                styleElement.remove();
            }
        }

        return () => {
            const element = document.getElementById(styleId);
            if (element) {
                element.remove();
            }
        };
    }, [state.searchTerm]);

    const messages = (t && t.multiselect) || {};
    const staticNames = (t && t.reportPage) || {};
    const staticActions = (t && t.actions) || {};

    if (!editing) {
        return (
            <div className="multi-value">
                <MultiValueView
                    values={state.values}
                    placeholder={staticNames.addValues}
                    editLabel={staticActions.edit || 'Edit'}
                    onEdit={startEdit}
                />
            </div>
        );
    }

    // keep the focus in the input while pressing the apply/cancel buttons
    const keepFocus = (event) => event.preventDefault();

    return (
        <div className="multi-value multi-value--editing" ref={wrapperRef} onMouseDownCapture={activate} onTouchStartCapture={activate} onFocusCapture={activate}>
        <Multiselect
            className={state.searchTerm ? '' : 'rw-multiselect-custom'}
            placeholder={staticNames.addValues}
            value={state.values}
            dataKey="value"
            textField="title"
            data={[]}
            searchTerm={state.searchTerm}
            onChange={onChange}
            onCreate={onCreate}
            onSearch={onSearch}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            open={false}
            inputProps={numericOnly ? { inputMode: 'numeric', pattern: '[0-9]*' } : undefined}
            messages={{
                ...messages,
                createOption: messageCreateOption,
                emptyList: ''
            }}
        />
            {!state.values.length && !state.searchTerm ? <span className="multi-value__search-icon" aria-hidden="true"><i className="fa fa-search" /></span> : null}
            {active ? (
            <div className="multi-value__actions">
                <button
                    type="button"
                    className="multi-value__action multi-value__action--apply"
                    title={staticActions.apply || 'Apply'}
                    aria-label={staticActions.apply || 'Apply'}
                    onMouseDown={keepFocus}
                    onClick={finishEdit}
                >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" stroke="currentColor" strokeWidth="1"/>
                    </svg>
                </button>
                <button
                    type="button"
                    className="multi-value__action multi-value__action--cancel"
                    title={staticActions.cancel || 'Cancel'}
                    aria-label={staticActions.cancel || 'Cancel'}
                    onMouseDown={keepFocus}
                    onClick={cancelEdit}
                >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" stroke="currentColor" strokeWidth="1"/>
                    </svg>
                </button>
            </div>
            ) : null}
        </div>
    );
};

Custom.propTypes = {
    segment: PropTypes.string,
    values: PropTypes.array,
    onAction: PropTypes.func,
    numericOnly: PropTypes.bool,
    t: PropTypes.object
};

Custom.defaultProps = {
    segment: '',
    values: [],
    onAction: () => {},
    t: {}
};

export default withTranslation(Custom);
